import React, { useCallback, useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { X, Loader2, ShieldCheck } from "lucide-react";
import api, { apiError } from "../lib/api";

const L = {
  it: { title: "Pagamento sicuro", loading: "Preparazione del pagamento…", secure: "Pagamenti protetti con Stripe", error: "Impossibile avviare il pagamento" },
  en: { title: "Secure payment", loading: "Preparing checkout…", secure: "Payments secured by Stripe", error: "Unable to start checkout" },
};

// Cache del publishable key -> promise di Stripe (una sola inizializzazione)
let _stripePromise = null;
let _cachedPk = null;
function getStripe(pk) {
  if (!pk) return null;
  if (_stripePromise && _cachedPk === pk) return _stripePromise;
  _cachedPk = pk;
  _stripePromise = loadStripe(pk);
  return _stripePromise;
}

export default function EmbeddedCheckoutModal({ lookupKey, lang = "it", onClose }) {
  const t = L[lang] || L.it;
  const [pk, setPk] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/payments/plans")
      .then(({ data }) => { if (data.publishable_key) setPk(data.publishable_key); else setError(t.error); })
      .catch(() => setError(t.error));
  }, [t.error]);

  const stripePromise = useMemo(() => getStripe(pk), [pk]);

  // Blocca lo scroll della pagina di sfondo così si scorre SOLO il checkout Stripe
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const fetchClientSecret = useCallback(async () => {
    const { data } = await api.post("/payments/checkout", {
      lookup_key: lookupKey,
      origin_url: window.location.origin,
      embedded: true,
    });
    if (!data.client_secret) throw new Error("no client secret");
    return data.client_secret;
  }, [lookupKey]);

  const options = useMemo(() => ({ fetchClientSecret }), [fetchClientSecret]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="embedded-checkout-modal">
      <div className="bg-white w-full max-w-xl rounded-t-2xl sm:rounded-2xl my-0 sm:my-6 overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 bg-[#0B0F17] text-white">
          <div className="flex items-center gap-2 font-head font-semibold text-sm">
            <ShieldCheck className="w-4 h-4 text-emerald-400" /> {t.title}
          </div>
          <button onClick={onClose} data-testid="embedded-checkout-close" className="p-1.5 rounded-lg hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>
        <div className="min-h-[420px] bg-white">
          {error ? (
            <div className="p-8 text-center text-sm text-red-600" data-testid="embedded-checkout-error">{error}</div>
          ) : !pk || !stripePromise ? (
            <div className="p-10 flex flex-col items-center justify-center gap-3 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="text-sm">{t.loading}</span>
            </div>
          ) : (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </div>
        <div className="px-5 py-2.5 bg-slate-50 border-t border-slate-200 text-center text-[11px] text-slate-500 flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> {t.secure}
        </div>
      </div>
    </div>
  );
}
